import { LinkSlashIcon, TrashIcon } from "@heroicons/react/20/solid";
import {
  extractApiErrorMessage,
  useDeleteTransportMutation,
  useDeleteTtaAllMutation,
  useDeleteTtaMutation,
  useUpdateTransportMutation,
  useValidateTtasMutation,
} from "@ots-frontend/ots-data";
import { useState } from "react";
import { useOptionalOtsToast } from "../../components/OtsToast";
import { ConfirmationDialog } from "../../dialogs/ConfirmationDialog";
import type { OtsBrand, OtsColorSchemePreference } from "../../theme/OtsTheme";
import type { DriverActivityAction, DriverActivityActionProvider } from "../driver-activity-actions";
import type { ActivityEntry } from "../driver-activity-view-model";

export type UseDriverActivityTicketEditToolsOptions = {
  /** Whether this person holds a permission; everything is offered when omitted. */
  can?: (permission: string) => boolean;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
};

type Confirmation = { kind: "delete" | "unmatch"; entries: ActivityEntry[] };

const tickets = (entries: ActivityEntry[]) => entries.filter((entry) => entry.tta);
const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? "" : "s"}`;
/** Unmatched pickups and deliveries, which are deleted as transports rather than tickets. */
const loneTransports = (entries: ActivityEntry[]) =>
  entries.filter((entry) => !entry.tta && (entry.pickup ?? entry.delivery)).map((entry) => (entry.pickup ?? entry.delivery)!);

/**
 * The ticket grid's edits for Driver Activity: dispatch validation, demurrage, unmatching a pickup from its
 * delivery, and deleting. Each patches the loaded day in place rather than fetching it again.
 */
export const useDriverActivityTicketEditTools = ({
  can = () => true,
  brand,
  colorScheme,
}: UseDriverActivityTicketEditToolsOptions = {}): DriverActivityActionProvider => {
  const toast = useOptionalOtsToast();
  const [validateTtas] = useValidateTtasMutation();
  const [updateTransport] = useUpdateTransportMutation();
  const [deleteTtaAll] = useDeleteTtaAllMutation();
  const [deleteTta] = useDeleteTtaMutation();
  const [deleteTransport] = useDeleteTransportMutation();
  const [confirmation, setConfirmation] = useState<Confirmation>();
  const [working, setWorking] = useState(false);

  const fail = (title: string, failure: unknown) =>
    toast?.show({ title, description: extractApiErrorMessage(failure) ?? "Please try again.", tone: "danger" });

  const setDispatchValidated = async (entries: ActivityEntry[], dispatchValidated: boolean) => {
    const targets = tickets(entries).filter((entry) => !!entry.tta?.dispatchValidatedDttm !== dispatchValidated);
    try {
      await validateTtas({
        validations: targets.map((entry) => ({ ticketId: entry.tta!.id, serviceLookup: entry.tta!.database, validationDTO: { dispatchValidated } })),
      }).unwrap();
      toast?.show({ title: `${plural(targets.length, "ticket")} ${dispatchValidated ? "validated" : "invalidated"}`, tone: "success" });
    } catch (failure) {
      fail(dispatchValidated ? "Validation failed" : "Invalidation failed", failure);
    }
  };

  const setDemurrage = async (entry: ActivityEntry | undefined, side: "pickup" | "delivery", disableDemurrage: boolean) => {
    const transport = entry?.[side];
    if (!transport) return;
    // The whole transport is saved, as the ticket form does, less the comments and gauges it keeps separately.
    const current = Object.fromEntries(Object.entries(transport).filter(([key]) => key !== "messages" && key !== "gauges"));
    try {
      await updateTransport({ ...current, disableDemurrage }).unwrap();
    } catch (failure) {
      fail("Demurrage could not be changed", failure);
    }
  };

  const confirm = async () => {
    if (!confirmation) return;
    setWorking(true);
    const { kind, entries } = confirmation;
    const requests = kind === "unmatch"
      ? tickets(entries).map((entry) => deleteTta({ id: entry.tta!.id }).unwrap())
      : [
        ...tickets(entries).map((entry) => deleteTtaAll({ id: entry.tta!.id }).unwrap()),
        ...loneTransports(entries).map((transport) => deleteTransport({ id: transport.id }).unwrap()),
      ];
    const results = await Promise.allSettled(requests);
    setWorking(false);
    setConfirmation(undefined);
    const failed = results.filter((result) => result.status === "rejected");
    if (failed.length) fail(`${plural(failed.length, kind === "unmatch" ? "ticket" : "record")} could not be ${kind === "unmatch" ? "unmatched" : "deleted"}`, (failed[0] as PromiseRejectedResult).reason);
    else toast?.show({ title: kind === "unmatch" ? "Pickup and delivery unmatched" : `${plural(results.length, "record")} deleted`, tone: "success" });
  };

  const demurrage = (side: "pickup" | "delivery", disable: boolean): DriverActivityAction => ({
    id: `${disable ? "disable" : "enable"}-${side}-demurrage`,
    label: `${disable ? "Disable" : "Enable"} ${side === "pickup" ? "Pickup" : "Delivery"} Demurrage`,
    scope: "single",
    group: "tools",
    isVisible: ([entry]) => !!entry?.[side] && !!entry[side]!.disableDemurrage !== disable,
  });

  type Definition = { action: DriverActivityAction; permission: string; run: (entries: ActivityEntry[]) => void };
  const definitions: Definition[] = [
    {
      action: {
        id: "dispatch-validate", label: "Validate", scope: "any", variant: "tonal",
        isVisible: (entries) => tickets(entries).length > 0,
        isEnabled: (entries) => tickets(entries).some((entry) => !entry.tta?.dispatchValidatedDttm),
      },
      permission: "transporttransportassoc_edit",
      run: (entries) => void setDispatchValidated(entries, true),
    },
    {
      action: {
        id: "dispatch-invalidate", label: "Invalidate", scope: "any", group: "tools",
        isVisible: (entries) => tickets(entries).length > 0,
        isEnabled: (entries) => tickets(entries).some((entry) => !!entry.tta?.dispatchValidatedDttm),
      },
      permission: "transporttransportassoc_edit",
      run: (entries) => void setDispatchValidated(entries, false),
    },
    ...(["pickup", "delivery"] as const).flatMap((side) => [true, false].map((disable): Definition => ({
      action: demurrage(side, disable),
      permission: "transporttransportassoc_edit",
      run: ([entry]) => void setDemurrage(entry, side, disable),
    }))),
    {
      action: {
        id: "unmatch", label: "Unmatch Pickup and Delivery", scope: "single", group: "tools",
        isVisible: ([entry]) => !!entry?.tta?.pickupTransport && !!entry.tta.deliveryTransport,
      },
      permission: "transporttransportassoc_delete",
      run: (entries) => setConfirmation({ kind: "unmatch", entries }),
    },
    {
      action: {
        id: "delete", label: "Delete", scope: "any", variant: "destructive",
        isVisible: (entries) => entries.every((entry) => entry.tta || entry.pickup || entry.delivery),
      },
      permission: "transporttransportassoc_delete",
      run: (entries) => setConfirmation({ kind: "delete", entries }),
    },
  ];
  const offered = definitions.filter((definition) => can(definition.permission));

  const ticketCount = confirmation ? tickets(confirmation.entries).length : 0;
  const transportCount = confirmation ? loneTransports(confirmation.entries).length : 0;
  return {
    actions: offered.map((definition) => definition.action),
    handleAction: (actionId, entries) => {
      const definition = offered.find((candidate) => candidate.action.id === actionId);
      if (!definition) return false;
      definition.run(entries);
      return true;
    },
    dialogs: (
      <ConfirmationDialog
        open={!!confirmation}
        tone="danger"
        icon={confirmation?.kind === "unmatch" ? <LinkSlashIcon/> : <TrashIcon/>}
        title={confirmation?.kind === "unmatch" ? "Unmatch this pickup and delivery?" : "Delete these records?"}
        description={confirmation?.kind === "unmatch"
          ? "The ticket is removed; its pickup and delivery stay, each on its own."
          : [ticketCount && `${plural(ticketCount, "ticket")}, with their pickups and deliveries`, transportCount && plural(transportCount, "unmatched transport")]
            .filter(Boolean).join(" and ") + " will be deleted. This can't be undone."}
        confirmLabel={confirmation?.kind === "unmatch" ? "Unmatch" : "Delete"}
        confirmingLabel={confirmation?.kind === "unmatch" ? "Unmatching…" : "Deleting…"}
        isConfirming={working}
        onConfirm={() => void confirm()}
        onCancel={() => setConfirmation(undefined)}
        brand={brand}
        colorScheme={colorScheme}/>
    ),
  };
};
